import { useEffect, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  addDays,
  addWeeks,
  endOfWeek,
  format,
  getWeek,
  isSameDay,
  startOfWeek,
  subDays,
  subWeeks,
} from "date-fns";
import { AnimatePresence, motion } from "motion/react";
import { Trans, useTranslation } from "react-i18next";

import { cn } from "~/lib/utils";
import MonthCalendar from "../booking/month-calendar";
import { Button } from "../ui/button";

const VIEWS = {
  day: "day",
  week: "week",
} as const;

type View = keyof typeof VIEWS;

function WeekView({ date }: { date: Date }) {
  const { t } = useTranslation("common");

  return (
    <div className="flex flex-col">
      <p className="text-2xl">{t("week", { week: getWeek(date) })}</p>
      <p className="text-base">{`${startOfWeek(date).getDate()} - ${endOfWeek(date).getDate()} ${format(date, "LLLL")}`}</p>
    </div>
  );
}

export function DateNav({
  date,
  onNavigate,
  view = VIEWS.day,
  className,
  futureDates,
  pastDates,
}: {
  date: Date;
  onNavigate: (date: Date) => void;
  view?: View;
  className?: string;
  futureDates?: boolean;
  pastDates?: boolean;
}) {
  const [mounted, setMounted] = useState(false);

  const handleCalendar = (nextDate: Date) => {
    onNavigate(nextDate);
    setCalendarOpen(false);
  };

  const handleNavigate = (prev: boolean) => {
    if (prev) {
      if (view === VIEWS.day) {
        console.log(subDays(date, 1));
        onNavigate(subDays(date, 1));
      } else {
        onNavigate(subWeeks(date, 1));
      }
    } else if (view === VIEWS.day) {
      onNavigate(addDays(date, 1));
    } else {
      onNavigate(addWeeks(date, 1));
    }
  };

  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="flex flex-col gap-2 py-1">
      <AnimatePresence>
        {calendarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[999] overflow-auto small-scrollbar"
          >
            <MonthCalendar
              onDayClick={(date) => {
                setCalendarOpen(false);
                handleCalendar(date);
              }}
              selectedDate={date}
              onClose={() => setCalendarOpen(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className={cn("flex flex-row items-center gap-4", className)}>
        <div className="flex flex-row items-center gap-1 relative">
          {pastDates && (
            <div className="hidden flex-row items-center gap-2 md:flex">
              <Button
                onClick={() => handleCalendar(subDays(new Date(), 3))}
                variant={
                  isSameDay(date, subDays(new Date(), 3)) ? "default" : "ghost"
                }
              >
                {format(subDays(new Date(), 3), "iiiiii")}
              </Button>
              <Button
                onClick={() => handleCalendar(subDays(new Date(), 2))}
                variant={
                  isSameDay(date, subDays(new Date(), 2)) ? "default" : "ghost"
                }
              >
                {format(subDays(new Date(), 2), "iiiiii")}
              </Button>
              <Button
                onClick={() => handleCalendar(subDays(new Date(), 1))}
                variant={
                  isSameDay(date, subDays(new Date(), 1)) ? "default" : "ghost"
                }
              >
                {format(subDays(new Date(), 1), "iiiiii")}
              </Button>
            </div>
          )}
          <Button
            onClick={() => {
              handleNavigate(true);
            }}
            variant="ghost"
          >
            <ChevronLeftIcon />
          </Button>

          <Button
            onClick={() => setCalendarOpen(!calendarOpen)}
            variant="ghost"
            className={cn(
              "w-[127px] text-md flex gap-2 font-medium",
              view === VIEWS.week && "w-[250px]",
            )}
          >
            {view === VIEWS.day && mounted ? (
              <Trans
                i18nKey="DateNav.date"
                values={{
                  day_name: format(date, "iiiiii"),
                  day_date: format(date, "dd"),
                  month_name: format(date, "LLL"),
                }}
                components={{
                  big: <span key="big" className="text-xl font-bold" />,
                }}
              />
            ) : null}
            {view === VIEWS.week && mounted ? <WeekView date={date} /> : null}
          </Button>

          <Button
            onClick={() => {
              handleNavigate(false);
            }}
            variant="ghost"
          >
            <ChevronRightIcon />
          </Button>
        </div>
        {futureDates && (
          <div className="hidden flex-row items-center gap-2 md:flex">
            <Button
              onClick={() => handleCalendar(addDays(new Date(), 1))}
              variant={
                isSameDay(date, addDays(new Date(), 1)) ? "default" : "ghost"
              }
            >
              {format(addDays(new Date(), 1), "iiiiii")}
            </Button>
            <Button
              onClick={() => handleCalendar(addDays(new Date(), 2))}
              variant={
                isSameDay(date, addDays(new Date(), 2)) ? "default" : "ghost"
              }
            >
              {format(addDays(new Date(), 2), "iiiiii")}
            </Button>
            <Button
              onClick={() => handleCalendar(addDays(new Date(), 3))}
              variant={
                isSameDay(date, addDays(new Date(), 3)) ? "default" : "ghost"
              }
            >
              {format(addDays(new Date(), 3), "iiiiii")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
